import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "First steps with Octave and machine learning",
  "description": "",
  "published": "2011-11-04T00:00:00.000Z",
  "redirect_from": ["/blog/first-steps-with-octave-and-machine-learning/swizec/2865"],
  "categories": "Artificial intelligence, Gradient descent, Logistic regression, Machine learning, Math, Octave, Optimization (mathematics), Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`You know how most programmers find functional coding to be ever so slightly mind bending and how it's somewhat difficult to wrap one's head around working with variables whose state you cannot change and lazy evaluations and all manner of odd things?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/11/300px-Gradient_ascent_%28surface%29.png",
        "alt": "The gradient descent algorithm in action. (2: ...",
        "title": "The gradient descent algorithm in action. (2: ..."
      }}></img></p>
    <p>{`The thing I've had most trouble with and still do actually, is coding in a functionally clean manner, using more recursion, cleaner abstractions and so on. Just as I thought I was almost starting to get kind of good at this, a bunch of people proved me wrong when I `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/crowdsourcing-elegance/swizec/2608",
        "title": "Crowdsourcing elegance"
      }}>{`crowdsourced some elegance`}</a>{`.`}</p>
    <p>{`Yeah, some people are `}<em parentName="p">{`really`}</em>{` good at this functional stuff.`}</p>
    <p>{`And then one day ml-class introduced me to `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Optimization_%28mathematics%29",
        "title": "Optimization (mathematics)"
      }}>{`mathematical programming`}</a>{` with Octave. Sure, I've done some Octave before at school, but that was just enough to get my feet wet - basic syntax and stuff. Or maybe I just paying enough attention to really grasp the awesome things I was being shown.`}</p>
    <p>{`Either way, I feel as if over the past two weeks, doing `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Machine_learning",
        "title": "Machine learning"
      }}>{`machine learning`}</a>{` homework in Octave has opened a whole new world of striving for elegance and purity in my code. If I thought functional was mindbending, this stuff is `}<em parentName="p">{`ripping my face off`}</em>{`.`}</p>
    <p>{`Apparently when you take a naive loop and make it into something beautiful it's called vectorization in this field. The interesting bit here is that all you really need to perform optimization of epic proportions is some math fu, no translating the problem into something else, no looking at it from five different perspectives ... just maths and then some.`}</p>
    <p>{`Using the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Gradient_descent",
        "title": "Gradient descent"
      }}>{`gradient descent`}</a>{` algorithm for `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Logistic_regression",
        "title": "Logistic regression"
      }}>{`logistic regression`}</a>{` as an example, in particular calculating the cost function:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/11/Screen-Shot-2011-11-04-at-12.32.27-PM.png",
        "alt": null,
        "title": "Professor Ng explaining the cost function"
      }}></img></p>
    <p>{`Although I think this might have been exactly the perfect example for the code below ... it's difficult to search through videos for this stuff.`}</p>
    <p>{`The naive approach could be something like this (didn't actually run the code):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-octave"
      }}>{`J = 0;
for i = 1:m
  J += =y(i)*log(sigmoid(theta*X(i,:))-(1-y(i)*log(1-sigmoid(theta*X(i,:));
end

J = J/m;

for j = 1:size(theta)
   grad(j) = 0;
   for i = 1:m
      grad(j) += (sigmoid(theta*X(i,:))-y(i))*X(i,j);
   end
   grad(j) = grad(j)/m;
end
`}</code></pre>
    <p>{`But all those lops aren't really necessary, all they are basically doing is `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Matrix_multiplication",
        "title": "Matrix multiplication"
      }}>{`matrix multiplication`}</a>{`, which gives us a nice way to vectorize the whole thing:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-octave"
      }}>{`J = (1/m)*sum(-y'*log(sigmoid(X*theta))-(1-y')*log(1-sigmoid(X*theta)));

grad = (1/m)*(sigmoid(X*theta)-y)'*X;
`}</code></pre>
    <p>{`The difference in elegance absolutely blows my mind and I can't wait to see what other wonders I discover through this Octave thing in the course of this semester.`}</p>
    <p>{`Pretty much all my octave can be found in the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/ml-class-homework"
      }}>{`ml-class-homework repository`}</a>{`. But I'm sure I'll end up modeling more algorithms in this thing.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://flowingmotion.jojordan.org/2011/10/16/12-steps-to-running-gradient-descent-in-octave/"
        }}>{`12 steps to running gradient descent in Octave`}</a>{` (flowingmotion.jojordan.org)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.r-bloggers.com/machine-learning-ex4-%e2%80%93-logistic-regression/"
        }}>{`Machine Learning Ex4 - Logistic Regression`}</a>{` (r-bloggers.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.r-bloggers.com/machine-learning-ex-5-2-%e2%80%93-regularized-logistic-regression/"
        }}>{`Machine Learning Ex 5.2 - Regularized Logistic Regression`}</a>{` (r-bloggers.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=821e177f-151f-4e6b-9a26-30e7b37cf580",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      